import { useMemo, useState, useEffect } from 'react';

import cn from 'classnames';

import styles from './OrbitSlider.module.scss';

import {
  baseClass,
  Colors,
  DotSizes,
  INNER_ORBIT_BIG_INDEXES,
  OrbitConfig,
  OrbitDefaults,
  OrbitSliderDimensions,
  OUTER_ORBIT_BIG_INDEXES,
  ViewBoxDimensions,
} from './OrbitSliderConstants';

type PropsOrbit = {
  radius: number;
  count: number;
  bigIndexes?: number[];
  duration?: number;
  reverse?: boolean;
};

type Props = {
  className?: string;
};

const Orbit = ({
  radius,
  count,
  bigIndexes = [],
  duration = OrbitDefaults.DEFAULT_DURATION,
  reverse = false,
}: PropsOrbit) => {
  const isOuterOrbit = radius === OrbitConfig.OUTER_RADIUS;

  const dots = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const angle = (2 * Math.PI * i) / count;
        const x = Math.ceil(Math.cos(angle) * radius);
        const y = Math.ceil(Math.sin(angle) * radius);
        const isBig = bigIndexes.includes(i);

        if (isBig) {
          return (
            <g key={i}>
              <circle
                className={styles[`${baseClass}-glow`]}
                cx={x}
                cy={y}
                r={isOuterOrbit ? DotSizes.OUTER_LARGE_GLOW : DotSizes.INNER_LARGE_GLOW}
                fill={Colors.LIGHT_BLUE}
              />
              <circle
                cx={x}
                cy={y}
                r={isOuterOrbit ? DotSizes.OUTER_MEDIUM : DotSizes.INNER_MEDIUM}
                fill={Colors.BLUE}
              />
            </g>
          );
        }

        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={isOuterOrbit ? DotSizes.OUTER_SMALL : DotSizes.INNER_SMALL}
            fill={Colors.LIGHT_BLUE}
          />
        );
      }),
    [],
  );

  return (
    <g
      className={cn(styles[`${baseClass}-orbit`], reverse && styles[`${baseClass}-reverse`])}
      style={{ animationDuration: `${duration}s` }}
    >
      <circle cx="0" cy="0" r={radius} fill="none" />
      {dots}
    </g>
  );
};

export const OrbitSlider = ({ className }: Props) => {
  const [lazyMounted, setLazyMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLazyMounted(true);
    }, 1500);
  }, []);

  return (
    <div className={cn(styles[`${baseClass}-wrapper`], className)}>
      <svg
        viewBox={`${ViewBoxDimensions.MIN_X} ${ViewBoxDimensions.MIN_Y} ${ViewBoxDimensions.WIDTH} ${ViewBoxDimensions.HEIGHT}`}
        width={ViewBoxDimensions.WIDTH}
        height={OrbitSliderDimensions.VISIBLE_HEIGHT}
        preserveAspectRatio="xMidYMin slice"
      >
        {lazyMounted && (
          <>
            <defs>
              <clipPath id="orbit-clip">
                <rect
                  x={ViewBoxDimensions.MIN_X}
                  y={ViewBoxDimensions.MIN_Y}
                  width={ViewBoxDimensions.WIDTH}
                  height={OrbitSliderDimensions.VISIBLE_HEIGHT}
                />
              </clipPath>
            </defs>

            <g clipPath="url(#orbit-clip)">
              <Orbit
                radius={OrbitConfig.INNER_RADIUS}
                count={OrbitConfig.INNER_COUNT}
                bigIndexes={INNER_ORBIT_BIG_INDEXES}
                duration={OrbitConfig.INNER_DURATION}
                reverse
              />

              <Orbit
                radius={OrbitConfig.OUTER_RADIUS}
                count={OrbitConfig.OUTER_COUNT}
                bigIndexes={OUTER_ORBIT_BIG_INDEXES}
                duration={OrbitConfig.OUTER_DURATION}
              />
            </g>
          </>
        )}
      </svg>
    </div>
  );
};
